import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import { PropOptions } from 'vue';
import { createPhoneFormatOptions, PhoneFormatOptions } from '../../../common/types/AllTypesExports';
import StringHelpers from '../../../common/services/StringHelpers';


@Component({
    name: 'PhoneInput'
})
export default class PhoneInput extends Vue {
    @Prop({ required: true, default: '' } as PropOptions<string>)
    value!: string;

    @Prop({ required: true, default: '' } as PropOptions<string>)
    phoneLabel!: string;

    @Prop({ required: false, default: true } as PropOptions<boolean>)
    includeParens!: boolean;

    @Prop({ required: false, default: false } as PropOptions<boolean>)
    allowExtensions!: boolean;

    @Prop({ required: false, default: '-' } as PropOptions<string>)
    delimiterText!: string;

    @Prop({ required: false, default: 'x' } as PropOptions<string>)
    extensionPrefix!: string;

    @Prop({ required: false, default: 21 } as PropOptions<number>)
    max!: number;

    @Prop({ required: false, default: false } as PropOptions<boolean>)
    isRequired!: boolean;

    @Prop({ required: false, default: null } as PropOptions<string>)
    validatorScope!: string;

    @Prop({ required: false, default: '' } as PropOptions<string>)
    id!: string;

    @Prop({ required: false, default: false } as PropOptions<boolean>)
    disabled!: boolean;

    @Prop({ required: false, default: false } as PropOptions<boolean>)
    allowInternational!: boolean;

    @Inject('validator')
    $validator: any;

    private localInternational: boolean = this.allowInternational;
    currentValue: string = '';
    cleanExtensionEvents = ['deleteContentBackward', 'deleteContentForward', 'insertFromPaste'];

    get placeholderText() {
        const stringBuilder: string[] = [];

        if (!this.localInternational) {
            stringBuilder.push(this.includeParens ? "(###)" : `###${this.delimiterText}`)
            stringBuilder.push(`###${this.delimiterText}####`);
            stringBuilder.push(this.allowExtensions ? ` ${this.extensionPrefix}#######` : "");
        }
        else {
            stringBuilder.push("###-###-####...");
        }

        const result = stringBuilder.join('');
        return result;
    }

    get showLabel() {
        return !this.phoneLabel ? false : true;
    }

    get rules() {
        const intlRegex = /^(?=(?:\D*\d){6,15}\D*$)(?:\+|00|011)?\s*(?:\(?\+?\d{1,4}\)?[\s\-.]*)?(?:[\d()\s\-.]+)+(?:\s?(?:x|ext\.?)\s?\d+)?$/i;

        const nanpRegex = /^\s*(?:\(?\d{3}\)?[\s.\-]*\d{3}[\s.\-]*\d{4})(?:\s?(?:x|ext\.?)\s?\d{1,7})?\s*$/;

        const netPhoneRegex = this.localInternational ? intlRegex : nanpRegex;

        const baseRules: any = {
            required: this.isRequired,
            max: this.max,
            regex: netPhoneRegex
        };

        return baseRules;
    }

    get minLength() {
        return this.localInternational ? 1 : 10 + (this.includeParens ? 2 : 0) + this.delimiterText.length;
    }

    emitInput(evt: any, currentId: string) {
        if (currentId !== this.id) return;

        let raw = this.value;

        // This checks to see if the user is entering a backspace, delete key, or paste
        if (evt && this.cleanExtensionEvents.includes(evt.inputType)) {
            const isPaste = evt.inputType === 'insertFromPaste';

            if (isPaste) {
                raw = this.cleanExtension(false);
            } else {
                this.cleanExtension(true);
                return;
            }
        }

        const formatOptions = createPhoneFormatOptions(<PhoneFormatOptions>{
            allowExtensions: this.allowExtensions,
            allowInternational: this.localInternational,
            delimiterText: this.delimiterText,
            includeParens: this.includeParens,
            extensionPrefix: this.extensionPrefix,
            maxCharacters: this.max
        });

        const next = StringHelpers.formatPhone(raw, formatOptions);
        this.currentValue = next;
        this.$emit('input', next);
    }

    private formatAndEmit(raw: string, intl: boolean) {
        let formatted = raw;

        if (!intl) {
            // Strict NANP masking: (###)###-#### [x #######]
            const opts: PhoneFormatOptions = createPhoneFormatOptions({
                allowExtensions: this.allowExtensions,
                allowInternational: false,
                delimiterText: this.delimiterText || '-',
                extensionPrefix: 'x',
                includeParens: true,
                maxCharacters: 25, // enough for ext
            });
            formatted = StringHelpers.formatPhone(raw, opts);
        } else {
            // For international, just keep digits + basic punctuation and cap length
            const keep = /[0-9xX()+\-\.\s]/g;
            formatted = (raw.match(keep) || []).join('').slice(0, this.max);
        }

        this.currentValue = formatted;
        this.$emit('input', formatted);
    }

    focus(isFocused: boolean) {
        /**************
         * Preserve distinction between truly blank values and
         * in-progress phone numbers for legacy validation behavior.
         * currentValue is kept in sync with prop changes via watcher.
         **************/
        if (isFocused && !this.value) {
            if (this.currentValue) {
                this.$emit('input', this.currentValue);
            }
        }
        else if (!isFocused) {
            this.cleanExtension(true);

            let validateName = this.id;
            if (this.validatorScope) {
                validateName = this.validatorScope + "." + this.id;
            }

            this.$nextTick(() => {
                this.$validator.validate(validateName).then((isValid: boolean) => {
                    if (!isValid) return;

                    // Handle custom error message for min length
                    this.$validator.errors.remove(this.id, this.validatorScope);

                    const valueToCheck = this.currentValue;
                    if (valueToCheck && valueToCheck.length < this.minLength) {
                        this.$validator.errors.add({
                            field: this.id,
                            msg: `The ${this.phoneLabel} field must be at least 10 digits.`,
                            scope: this.validatorScope
                        });
                    }
                });
            });
        }
    }

    toggleAllowInternational() {
        const next = !this.localInternational;
        this.localInternational = next;
        this.$emit('update:allowInternational', next);
        this.formatAndEmit(this.value, next);
    }

    cleanExtension(emitUpdate: boolean): string {
        let next = (this.value || '').trim();

        if (next.endsWith('x')) {
            next = next.slice(0, -1).trim();
        }

        this.currentValue = next;

        if (emitUpdate) {
            this.$emit('input', next);
        }

        return next;
    }

    get isInternationalNumber() {
        if (!this.value)
            return this.localInternational;

        const isValueInternational = this.includeParens
            ? !(new RegExp(`^\\(\\d{3}\\)\\d{3}${this.delimiterText}\\d{4}` + (this.allowExtensions ? `( ${this.extensionPrefix}[\\s]?\\d{1,7})?` : "") + "$").test(this.value))
            : !(new RegExp(`^\\d{3}${this.delimiterText}\\d{3}${this.delimiterText}\\d{4}` + (this.allowExtensions ? `( ${this.extensionPrefix}[\\s]?\\d{1,7})?` : "") + "$").test(this.value));
        return isValueInternational;
    }

    mounted() {
        this.localInternational = this.isInternationalNumber;
        this.currentValue = this.value;
    }

    @Watch('value')
    onValueChanged(v: string) {
        const next = v || '';
        if (this.currentValue !== next) {
            this.currentValue = next;
        }
    }

    @Watch('allowInternational')
    onPropChanged(v: boolean) {
        this.localInternational = v;
    }
}